import axios from 'axios';

/**
 * Media-library API calls.
 *
 * Its own axios instance, mirroring api.js: same base URL, same Bearer token
 * from localStorage. Separate so the upload call can attach a per-request
 * onUploadProgress handler without touching the shared client.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const apiMedia = axios.create({ baseURL: API_BASE_URL });

apiMedia.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * POST /media/upload — multipart. Writes the file into the bucket's raw/ prefix
 * and creates the media doc immediately, so the returned item is renderable
 * before the resize webhook has produced thumbnails (processing: true).
 *
 * fields: { name, description, tags (array or comma string), source,
 *           contributor_name, editorial_only }
 * onProgress: (0-100) => void
 */
export const uploadMedia = async (file, fields = {}, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  if (fields.name) form.append('name', fields.name);
  if (fields.description) form.append('description', fields.description);
  if (fields.tags && fields.tags.length) {
    form.append('tags', Array.isArray(fields.tags) ? fields.tags.join(',') : fields.tags);
  }
  if (fields.source) form.append('source', fields.source);
  if (fields.contributor_name) form.append('contributor_name', fields.contributor_name);
  if (fields.editorial_only) form.append('editorial_only', 'true');

  const response = await apiMedia.post('/media/upload', form, {
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = event.total || file.size;
      if (!total) return;
      onProgress(Math.min(100, Math.round((event.loaded * 100) / total)));
    },
  });
  return response.data;
};

/** PATCH /media/{id} — editorial metadata only; returns the updated doc. */
export const patchMedia = async (mediaId, updates) => {
  const response = await apiMedia.patch(`/media/${mediaId}`, updates);
  return response.data;
};

/** GET /media/{id}/usage — { pages: [{domain_id, hostname, slug, title, role}], count }. */
export const getMediaUsage = async (mediaId) => {
  const response = await apiMedia.get(`/media/${mediaId}/usage`);
  return response.data;
};

/** DELETE /media/{id} — 409 with the usage payload when the image is in use. */
export const deleteMedia = async (mediaId, { recursive = true } = {}) => {
  const response = await apiMedia.delete(`/media/${mediaId}`, { params: { recursive } });
  return response.data;
};

export default apiMedia;
